import { Button } from '@plato/ui/primitives/button';
import { isMacOs, isWindows } from 'react-device-detect';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import { getShortcutKey } from '../../plato-user-layout/utils/get-shortcut-key.ts';
import { AiChatIcon } from '../assets/ai-chat-icon.tsx';
import { useAskPlato } from './ask-plato-provider.tsx';

/**
 * Desktop entry point: a floating action button in the bottom-right corner with
 * a tooltip on hover.
 *
 * Growth is animated as width/height rather than `scale`, because Tailwind's
 * var-chain utilities (`scale-*`, `shadow-*`) don't resolve in this app.
 *
 * The button retreats while the panel is open — the panel's own close button
 * takes over from there, and it would otherwise sit under the docked panel.
 */
export function AskPlatoFab() {
    const { t, locale } = useLocale('common');
    const { open, openAskPlato } = useAskPlato();

    const shortcutKey = getShortcutKey({ isMacOs, isWindows, locale, letter: 'J' });

    return (
        <div
            className={cn(
                // The row is as wide as the hover tooltip, which stays laid out
                // while invisible, so it must never take pointer events itself —
                // only the button does, or the strip swallows clicks meant for
                // whatever the page renders in this corner.
                'group pointer-events-none fixed bottom-6 right-6 z-30 hidden items-center justify-end sm:flex',
                'transition-opacity duration-200 ease-out',
                open && 'translate-y-2 opacity-0',
            )}
        >
            {/* tooltip */}
            <span
                className={cn(
                    'pointer-events-none mr-3 flex translate-x-1 items-center gap-2 whitespace-nowrap rounded-lg',
                    'bg-neutral-900 px-3 py-2 text-sm text-white opacity-0',
                    'transition-all duration-150 ease-out',
                    'group-hover:translate-x-0 group-hover:opacity-100',
                )}
            >
                {t('ask_plato__trigger')}
                <kbd className="rounded border border-white/25 px-1 font-sans text-xs text-white/70">
                    {shortcutKey}
                </kbd>
            </span>

            <Button
                variant="primary"
                intent="ai"
                aria-label={t('ask_plato__trigger')}
                onClick={() => {
                    openAskPlato('fab');
                }}
                className={cn(
                    'h-16 w-16 min-w-0 rounded-full p-0',
                    'transition-all duration-200 ease-out',
                    'group-hover:h-[4.5rem] group-hover:w-[4.5rem]',
                    !open && 'pointer-events-auto',
                )}
            >
                <AiChatIcon
                    className={cn(
                        'h-8 w-8 transition-all duration-200 ease-out',
                        'group-hover:h-9 group-hover:w-9',
                    )}
                />
            </Button>
        </div>
    );
}
